import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { EmailContent, EmailContentDTO } from "./emailContent";
/* eslint-disable import/export */
export abstract class PreviewEmailRPC implements
  LeoRPC<PreviewEmailRPC.Request, PreviewEmailRPC.Response, PreviewEmailRPC.Errors.Errors>
{
  abstract execute(
    request: PreviewEmailRPC.Request
  ): Promise<LeoRPCResult<PreviewEmailRPC.Response, PreviewEmailRPC.Errors.Errors>>;
}

export namespace PreviewEmailRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    emailId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isPreviewEmailRPCRequest: boolean = true;
  
    readonly emailId: LeoUUID;
  
  
    constructor(
    emailId: LeoUUID
    ) {
      this.emailId = emailId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const emailId = getLeoUuid(dto, "emailId", InvalidRequestEmailIdError);
      return new Request(
        emailId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          emailId: this.emailId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `PreviewEmailRPC(emailId='${this.emailId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    emailContent: EmailContentDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isPreviewEmailResponse: boolean = true;
  
    readonly emailContent: EmailContent;
  
  
    constructor(
    emailContent: EmailContent
    ) {
      this.emailContent = emailContent;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const emailContent = EmailContent.fromDTO(getObject(dto, "emailContent", InvalidResponseEmailContentError));
      return new Response(
        emailContent
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          emailContent: this.emailContent.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `PreviewEmail(emailContent='${this.emailContent}')`;
  };
  

  export enum RPCError {
    InvalidEmailId = "INVALID_EMAIL_ID",
    UnexpectedError = "UNEXPECTED_ERROR"
  }

  export namespace Errors {
    
    export interface InvalidEmailIdDTO {
      code: string;
    }
    
    export class InvalidEmailId extends LeoRPCError<RPCError> {
      readonly isInvalidEmailId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidEmailId;
        super(code);
      }
    
    
      toDTO(): InvalidEmailIdDTO {
        const dto: InvalidEmailIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidEmailId.prototype.toString = function toString(): string {
      return `PreviewEmailRPC.INVALID_EMAIL_ID()`;
    };
    
    
    export interface UnexpectedErrorDTO {
      code: string;
    }
    
    export class UnexpectedError extends LeoRPCError<RPCError> {
      readonly isUnexpectedError: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.UnexpectedError;
        super(code);
      }
    
    
      toDTO(): UnexpectedErrorDTO {
        const dto: UnexpectedErrorDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    UnexpectedError.prototype.toString = function toString(): string {
      return `PreviewEmailRPC.UNEXPECTED_ERROR()`;
    };
    

    export type Errors = InvalidEmailId | UnexpectedError;
    export type ErrorsDTO = InvalidEmailIdDTO | UnexpectedErrorDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidEmailId:
          return new InvalidEmailId();
        case RPCError.UnexpectedError:
          return new UnexpectedError();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestEmailIdError extends PreviewEmailRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmailIdError";
  }
}

export class InvalidResponseEmailContentError extends PreviewEmailRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseEmailContentError";
  }
}

export class InvalidResponseInvalidEmailIdError extends PreviewEmailRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidEmailIdError";
  }
}

export class InvalidResponseUnexpectedErrorError extends PreviewEmailRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseUnexpectedErrorError";
  }
}

